import React, { useCallback, useEffect, useRef, useState } from "react";

import { BackgroundColorBlack } from "ds/Alias";
import { Box } from "ds/Box";
import { Button } from "ds/Button";
import { Space200 } from "ds/Core";
import { Icon } from "ds/icons";
import { useUpdateAccountMutation } from "graphql/accounts";
import { useGlobalContext } from "hooks";
import { useLocaleContext } from "utils/context/locale/useLocaleContext";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const VIDEO_ID = "8LA7EXxiDUM";

export const WelcomeVideoModal = () => {
  const [showVideo, setShowVideo] = useState(false);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { locale } = useLocaleContext();

  const { currentOrganisation } = useGlobalContext();

  const account = currentOrganisation?.myAccount;

  useEffect(() => {
    if (account) {
      const shouldShowWelcomeVideo = !account?.viewedModals?.includes("welcome_video");
      setShowVideo(shouldShowWelcomeVideo);
    }
  }, [account]);

  const [updateAccount] = useUpdateAccountMutation();

  const handleCloseVideo = useCallback(() => {
    document.body.style.overflow = "auto";
    setShowVideo(false);

    updateAccount({
      variables: {
        accountUuid: account?.uuid!,
        accountUpdateInput: {
          viewedModals: [...(account?.viewedModals || []), "welcome_video"],
        },
      },
    });

    if (playerRef?.current && typeof playerRef.current.destroy === "function") {
      playerRef.current.destroy();
    }
  }, [account, updateAccount]);

  useEffect(() => {
    if (!showVideo) return;

    document.body.style.overflow = "hidden";

    const loadYouTubeAPI = () => {
      if (window.YT) {
        initializePlayer();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.body.appendChild(script);
      window.onYouTubeIframeAPIReady = initializePlayer;
    };

    const initializePlayer = () => {
      if (!containerRef.current) return;

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: VIDEO_ID,
        playerVars: {
          autoplay: 1,
          controls: 1,
          rel: 0,
          hl: locale,
        },
        events: {
          onStateChange: onPlayerStateChange,
        },
      });
    };

    const onPlayerStateChange = (event: any) => {
      if (event.data === 0) {
        handleCloseVideo();
      }
    };

    loadYouTubeAPI();
  }, [showVideo, locale, handleCloseVideo]);

  if (!showVideo) {
    return null;
  }

  return (
    <Box
      position="fixed"
      style={{ top: 0, bottom: 0, width: "100vw", zIndex: 9999, backgroundColor: BackgroundColorBlack }}
    >
      <div ref={containerRef} style={{ borderRadius: "4px", height: "100%", width: "100%" }} />
      <Button
        icon={<Icon name="XCloseIcon" />}
        variation="secondary"
        onClick={handleCloseVideo}
        style={{ position: "absolute", top: Space200, right: Space200 }}
      />
    </Box>
  );
};
